import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import ApiService from '../../services/api';

interface KPIData {
  oee: number;
  efficiency: number;
  qualityRate: number;
  activeAlerts: number;
}

interface TrendData {
  date: string;
  oee: number;
  availability: number;
  performance: number;
  quality: number;
}

interface EquipmentStatus {
  operational: number;
  warning: number;
  critical: number;
  maintenance: number;
}

interface DashboardState {
  kpiData: KPIData | null;
  trendData: TrendData[];
  equipmentStatus: EquipmentStatus | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
}


const initialState: DashboardState = {
  kpiData: null,
  trendData: [],
  equipmentStatus: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchData',
  async () => {
    const [analyticsResult, equipmentResult] = await Promise.allSettled([
      ApiService.getAnalytics(),
      ApiService.request('/analytics/equipment'),
    ]);

    // Parse analytics response for KPI and trend data
    let kpiData: KPIData | null = null;
    let trendData: TrendData[] = [];
    let equipmentStatus: EquipmentStatus | null = null;

    if (analyticsResult.status === 'fulfilled' && analyticsResult.value) {
      const analytics = analyticsResult.value;
      // analytics-ai executive dashboard shape: { data: { kpis: {...}, alerts: {...} } }
      const kpis = analytics.data?.kpis ?? analytics.data ?? analytics;
      const alerts = analytics.data?.alerts ?? {};

      kpiData = {
        oee: kpis.oee ?? analytics.oee ?? 0,
        efficiency: kpis.production_efficiency ?? kpis.efficiency ?? analytics.efficiency ?? 0,
        qualityRate: kpis.quality_rate ?? kpis.qualityRate ?? analytics.qualityRate ?? 0,
        activeAlerts: alerts.total ?? kpis.activeAlerts ?? analytics.activeAlerts ?? 0,
      };

      // Map trend data if API returns it
      if (Array.isArray(analytics.trends)) {
        trendData = analytics.trends.map((t: any) => ({
          date: t.date ?? t.period ?? '',
          oee: t.oee ?? 0,
          availability: t.availability ?? 0,
          performance: t.performance ?? 0,
          quality: t.quality ?? 0,
        }));
      } else if (Array.isArray(analytics.data?.trends)) {
        trendData = analytics.data.trends.map((t: any) => ({
          date: t.date ?? t.period ?? '',
          oee: t.oee ?? 0,
          availability: t.availability ?? 0,
          performance: t.performance ?? 0,
          quality: t.quality ?? 0,
        }));
      }
    }

    // Parse equipment status
    if (equipmentResult.status === 'fulfilled' && equipmentResult.value) {
      const eq = equipmentResult.value?.data ?? equipmentResult.value;
      if (eq && typeof eq === 'object') {
        equipmentStatus = {
          operational: eq.operationalEquipment ?? eq.operational ?? 0,
          warning: eq.warningEquipment ?? eq.warning ?? 0,
          critical: eq.criticalEquipment ?? eq.critical ?? 0,
          maintenance: eq.maintenanceEquipment ?? eq.maintenance ?? 0,
        };
      }
    }

    return { kpiData, trendData, equipmentStatus };
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateKPI: (state, action: PayloadAction<Partial<KPIData>>) => {
      if (state.kpiData) {
        state.kpiData = { ...state.kpiData, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.kpiData = action.payload.kpiData;
        state.trendData = action.payload.trendData;
        state.equipmentStatus = action.payload.equipmentStatus;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch dashboard data';
        // Leave kpiData/equipmentStatus as null so the UI shows "—" placeholders
        // rather than fake numbers.
      });
  },
});

export const { clearError, updateKPI } = dashboardSlice.actions;
export default dashboardSlice.reducer;
